import {
  AppCard,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { Box } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { CommentsTab } from "../-internal-components/tabs/CommentsTab";
import { DetailsTab } from "../-internal-components/tabs/DetailsTab";
import { MaterialTab } from "../-internal-components/tabs/MaterialTab";
import { rnmRequestLoaderQueryOptions } from "../-queries/rnmRequestQueries";
import { rnmRequestShowPageSearchSchema } from "../-schemas/rnmRequestShowPageSearchSchema";

const ShowRnmRequest: React.FC = () => {
  const { rnmRequestId } = Route.useParams();
  const { tab } = Route.useSearch();
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const { data: rnmRequest } = useSuspenseQuery(
    rnmRequestLoaderQueryOptions(rnmRequestId),
  );

  const boxRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  useEffect(() => {
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      shouldScrollRef.current = true;

      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    requestAnimationFrame(() => {
      boxRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const isEditable = useMemo(() => {
    if (
      !rnmRequest.mm_request.process_instance.completed &&
      (user?.roles_names.includes("Edit material requests") ||
        user?.id === rnmRequest.mm_request.assigned_to?.id)
    ) {
      return true;
    }
    return false;
  }, [
    rnmRequest.mm_request.assigned_to?.id,
    user?.id,
    user?.roles_names,
    rnmRequest.mm_request.process_instance.completed,
  ]);

  return (
    <PageContainer
      showBackButton
      title={`${t("common.rnmRequest")} #${rnmRequest.mm_request.id}`}
    >
      {!!rnmRequest && (
        <>
          <MmRequestHeader
            id={rnmRequest.mm_request.id}
            keysToInvalidate={["rnmRequests", "detail", Number(rnmRequestId)]}
          />
          <AppCard>
            <Box
              ref={boxRef}
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 3,
                width: "100%",
              }}
            >
              <MmRequestTabs
                onChange={handleTabChange}
                tabs={[
                  "details",
                  "materialList",
                  "attachments",
                  "comments",
                  "history",
                ]}
                value={tab}
              />
            </Box>

            <Box sx={{ width: "100%" }}>
              {tab === "details" && (
                <DetailsTab isEditable={isEditable} rnmRequest={rnmRequest} />
              )}
              {tab === "materialList" && (
                <MaterialTab
                  isEditable={isEditable}
                  isSurf={
                    rnmRequest.mm_request.requester_area === "Surf and Subsea"
                  }
                  RnmRequestId={rnmRequest.id}
                  rnmForms={rnmRequest.rnm_forms}
                />
              )}
              {tab === "attachments" && (
                <MmRequestAttachments
                  isCompleted={rnmRequest.mm_request.process_instance.completed}
                  mmRequestId={rnmRequest.mm_request.id}
                />
              )}
              {tab === "comments" && (
                <CommentsTab
                  isCompleted={rnmRequest.mm_request.process_instance.completed}
                  mmRequestId={rnmRequest.mm_request.id}
                />
              )}
              {tab === "history" && (
                <MmHistoryLogs mmRequestId={rnmRequest.mm_request.id} />
              )}
            </Box>
          </AppCard>
        </>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/rnm_requests/$rnmRequestId/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  component: ShowRnmRequest,
  validateSearch: rnmRequestShowPageSearchSchema,
  loaderDeps: ({ search }) => ({
    tab: search.tab,
  }),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("View Material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, params: { rnmRequestId } }) =>
    context.queryClient.ensureQueryData(
      rnmRequestLoaderQueryOptions(rnmRequestId),
    ),
});
